// src/FindPeople.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from '../../services/supabase';
import { FollowButton } from "../../components/buttons";
import "./FindPeople.css";

export default function FindPeople({ searchQuery: externalSearchQuery }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState(externalSearchQuery || '');
  const navigate = useNavigate();

  useEffect(() => {
    // Update searchTerm if external query changes
    if (externalSearchQuery !== undefined) {
      setSearchTerm(externalSearchQuery);
      setLocalSearchQuery(externalSearchQuery);
    }
  }, [externalSearchQuery]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      // Get the current user
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // Fetch all user profiles
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*");
        
      if (error) {
        console.error("Error fetching users:", error);
        setLoading(false);
        return;
      }
      
      // Use cached follower and following counts from user_profiles table
      const processedUsers = data.map((user) => {
        return {
          ...user,
          // Use cached counts from user_profiles table
          followersCount: user.follower_count || 0,
          followingCount: user.following_count || 0,
          isCurrentUser: currentUser?.id === user.user_id
        };
      });
      
      // Filter out the current user
      const filteredUsers = processedUsers.filter(user => !user.isCurrentUser);
      
      setUsers(filteredUsers);
      setFilteredUsers(filteredUsers);
      setLoading(false);
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
    } else {
      const lowerQuery = searchTerm.toLowerCase();
      const filtered = users.filter((user) => {
        const nameMatch = user.name?.toLowerCase().includes(lowerQuery);
        const usernameMatch = user.username?.toLowerCase().includes(lowerQuery);
        return nameMatch || usernameMatch;
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(localSearchQuery);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    // Enable real-time search as user types
    setSearchTerm(value);
  };

  // Handle follow status change
  const handleFollowChange = (userId, isFollowing) => {
    // Update the local state to immediately reflect the new follower count
    setUsers(prevUsers => 
      prevUsers.map(user => {
        if (user.user_id === userId) {
          return {
            ...user,
            followersCount: isFollowing 
              ? (user.followersCount || 0) + 1 
              : Math.max((user.followersCount || 0) - 1, 0)
          };
        }
        return user;
      })
    );
    
    // Also update filtered users for immediate UI feedback
    setFilteredUsers(prevUsers => 
      prevUsers.map(user => {
        if (user.user_id === userId) {
          return {
            ...user,
            followersCount: isFollowing 
              ? (user.followersCount || 0) + 1 
              : Math.max((user.followersCount || 0) - 1, 0)
          };
        }
        return user;
      })
    );
  };

  // Helper to go to profile by userId
  const goToProfile = async (userId, navigate) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('username')
      .eq('user_id', userId)
      .single();
    if (data && data.username) {
      navigate(`/profile/${data.username}`);
    } else {
      navigate('/profile');
    }
  };

  return (
    <div className="find-people-container">
      <h2>Find People</h2>
      
      <form className="search-form" onSubmit={handleSearch}>
        <input
          type="text"
          className="search-input"
          placeholder="Search by name or username"
          value={localSearchQuery}
          onChange={handleInputChange}
        />
        <button type="submit" className="search-button">Search</button>
      </form>
      
      {loading ? (
        <div className="loading-indicator">Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <p className="no-results">No users found {searchTerm ? `for "${searchTerm}"` : ""}</p>
      ) : (
        <div className="people-list">
          {filteredUsers.map((user) => (
            <div key={user.id} className="user-card">
              <div 
                className="user-card-clickable"
                onClick={() => goToProfile(user.user_id, navigate)}
              >
                <div className="user-card-avatar">
                  {user.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={`${user.username || 'User'}'s profile`}
                      className="user-avatar"
                    />
                  ) : (
                    <div className="user-avatar-placeholder">
                      {user.username ? user.username.charAt(0).toUpperCase() : "?"}
                    </div>
                  )}
                </div>
                
                <div className="user-card-info">
                  <h3 className="user-name">{user.username || "Unnamed User"}</h3>
                  {user.bio && <p className="user-bio">{user.bio}</p>}
                  <div className="user-stats">
                    <span className="stat">{user.followersCount || 0} followers</span>
                    <span className="stat">{user.destination_count || 0} destinations</span>
                  </div>
                </div>
              </div>
              
              <div className="user-card-actions">
                <FollowButton 
                  userId={user.user_id} 
                  onFollowChange={(isFollowing) => handleFollowChange(user.user_id, isFollowing)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
